import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { SEO, StructuredData } from '@/components/seo'

export default function CareersPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <SEO
        title="Careers - Join Our Team"
        description="Help us fix hiring. Join JumpstoneHR and work on cutting-edge AI applications that make real-world impact. We're building the future of hiring decisions."
        canonical="/careers"
        structuredData={[
          StructuredData.webPage(
            'Careers at JumpstoneHR',
            'Join our team and help build the future of AI-powered hiring decisions.',
            '/careers'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Careers', url: '/careers' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'EmployerAggregateRating',
            itemReviewed: {
              '@type': 'Organization',
              name: 'Jumpstone Technology Inc.',
              sameAs: 'https://jumpstonehr.com',
            },
            ratingValue: '4.9',
            bestRating: '5',
            worstRating: '1',
            ratingCount: '12',
          },
        ]}
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          {/* Gradient orbs */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-500/20 via-brand-600/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-brand-600/15 via-brand-500/5 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
          
          <div className="relative mx-auto max-w-4xl section-padding">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-brand-500" />
              <span className="text-brand-400 text-sm font-medium tracking-widest uppercase">Join Us</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-8">
              Help us fix{' '}
              <span className="text-brand-400">hiring</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-surface-300 font-light max-w-2xl">
              We're building the future of hiring decisions. Join a team that believes every candidate deserves fair, evidence-based evaluation.
            </p>
          </div>
        </section>

        {/* Why Join Section */}
        <section className="py-20 bg-surface-900">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-12">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Why Jumpstone</span>
              <h2 className="text-3xl font-bold mt-3 text-white">Build something meaningful</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-brand-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Early Stage Impact</h3>
                <p className="text-surface-400">
                  Join at the ground floor. Your work will directly shape the product and the company's direction.
                </p>
              </div>

              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-brand-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">AI-First Product</h3>
                <p className="text-surface-400">
                  Work on cutting-edge AI applications that make real-world impact on how companies hire.
                </p>
              </div>

              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-brand-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Small, Focused Team</h3>
                <p className="text-surface-400">
                  No bureaucracy. Work directly with founders and fellow builders who care deeply about the craft.
                </p>
              </div>

              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-brand-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Hybrid & Global</h3>
                <p className="text-surface-400">
                  Offices in Toronto, Seattle, Kilinochchi, Jaffna, and Colombo. Flexible hybrid work with in-person collaboration.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-24 bg-surface-950">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-12">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Open Positions</span>
              <h2 className="text-3xl font-bold mt-3 text-white">Current opportunities</h2>
            </div>

            <div className="bg-surface-900 border border-surface-800 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-500/10 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">No open positions right now</h3>
              <p className="text-surface-400 mb-8 max-w-md mx-auto">
                We're a small team and don't always have open roles. But if you're exceptional and believe in what we're building, we'd still love to hear from you.
              </p>
              <a 
                href="mailto:careers@jumpstonehr.com" 
                className="inline-flex items-center gap-2 btn bg-brand-600 text-white hover:bg-brand-500 px-6 py-3"
              >
                Get in touch
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 bg-surface-900">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-12">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">How We Work</span>
              <h2 className="text-3xl font-bold mt-3 text-white">Our values</h2>
            </div>

            <div className="space-y-6">
              {[
                {
                  title: 'Clarity over cleverness',
                  description: 'We value clear thinking and clear communication. Simple solutions that work beat complex ones that impress.',
                },
                {
                  title: 'Evidence over intuition',
                  description: 'We practice what we preach. Decisions are made with data and reasoning, not hierarchy or gut feelings.',
                },
                {
                  title: 'Ownership over delegation',
                  description: 'Everyone is responsible for outcomes, not just tasks. We trust each other to figure things out.',
                },
                {
                  title: 'Progress over perfection',
                  description: 'We ship early, learn fast, and iterate. Done is better than perfect, especially when we can improve it tomorrow.',
                },
              ].map((value, index) => (
                <div key={value.title} className="flex gap-6 items-start group">
                  <span className="text-brand-500 font-mono text-sm mt-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                    <p className="text-surface-400">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-surface-950 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-t from-brand-500/10 via-brand-600/5 to-transparent rounded-full blur-3xl" />
          </div>
          
          <div className="relative mx-auto max-w-4xl section-padding text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Think you'd be a great fit?
            </h2>
            <p className="text-xl text-surface-300 mb-8 max-w-2xl mx-auto">
              Send us an email telling us about yourself and what you'd want to work on. No cover letter templates, just be real.
            </p>
            <a 
              href="mailto:careers@jumpstonehr.com" 
              className="inline-flex items-center gap-2 btn bg-brand-600 text-white hover:bg-brand-500 px-8 py-4 text-lg"
            >
              careers@jumpstonehr.com
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

